/**
 * Where to read "is this adult a parent of this athlete?" from (#5915).
 *
 * An adult removed from Family Access keeps their parent_athlete_relationships
 * row as history. Two sources hide that row:
 * - A logged-in user's own client: the database's RLS already hides a revoked
 *   link (migration 20261270_001, restrictive policy revoked_guardian_no_access),
 *   so the table itself is right.
 * - The service-role client bypasses RLS, so it must read the view
 *   parent_athlete_relationships_live (the table minus revoked links). The view
 *   is granted to service_role only; a user client reading it gets "permission
 *   denied".
 *
 * Use parentLinkReadSource(client) wherever the same code may run with either
 * client. Code that only ever runs with the service role may name the view
 * directly. Writes always go to the table.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { fetchAllInChunks } from '@/utils/supabase/fetchInChunks';

export const PARENT_LINK_TABLE = 'parent_athlete_relationships' as const;
export const LIVE_PARENT_LINK_VIEW = 'parent_athlete_relationships_live' as const;

export type ParentLinkReadSource = typeof PARENT_LINK_TABLE | typeof LIVE_PARENT_LINK_VIEW;

/**
 * True when the client was built with the service-role key. Every service-role
 * client in this app is built from SUPABASE_SERVICE_ROLE_KEY; in the browser the
 * variable is absent, so this is always false there.
 */
export function isServiceRoleClient(client: SupabaseClient<any, any, any> | null | undefined): boolean {
  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!client || !serviceKey) return false;
  // supabaseKey is a protected field of SupabaseClient; parentLinkSource.test.ts
  // builds a real client so an SDK change that renames it fails a test.
  return (client as unknown as { supabaseKey?: string }).supabaseKey === serviceKey;
}

export function parentLinkReadSource(client: SupabaseClient<any, any, any> | null | undefined): ParentLinkReadSource {
  return isServiceRoleClient(client) ? LIVE_PARENT_LINK_VIEW : PARENT_LINK_TABLE;
}

/**
 * `client.from(...)` for a READ of parent links, from the right source. The view
 * has the table's columns and foreign keys, so it is typed as the table to keep
 * embeds (`athletes!inner(...)`) typed.
 */
export function fromParentLinks<C extends SupabaseClient<any, any, any>>(client: C) {
  return client.from(parentLinkReadSource(client) as typeof PARENT_LINK_TABLE);
}

/**
 * The athletes `parentId` was removed from through Family Access: link rows the
 * table still holds (history) that the live view hides. Use it to filter rows
 * the adult OWNS about an athlete (bills, payments, autopay, bookings…) when a
 * service-role client reads them — RLS hides those rows only for a user client.
 *
 * - User client: returns an empty set without reading (RLS already filters).
 * - An adult with no removed/revoked household row: one index probe, empty set.
 * - Returns null when a read fails; callers fail closed (refuse) rather than
 *   show a removed adult's rows.
 */
export async function getRevokedAthleteIdsForParent(
  client: SupabaseClient<any, any, any>,
  parentId: string
): Promise<Set<string> | null> {
  if (!isServiceRoleClient(client) || !parentId) return new Set();

  const { data: removedRows, error: removedError } = await (client as any)
    .from('family_guardians')
    .select('id')
    .eq('guardian_auth_user_id', parentId)
    .in('status', ['removed', 'revoked'])
    .limit(1);
  if (removedError) return null;
  if (!removedRows || removedRows.length === 0) return new Set();

  const [{ data: allLinks, error: allError }, { data: liveLinks, error: liveError }] = await Promise.all([
    (client as any).from(PARENT_LINK_TABLE).select('athlete_id').eq('parent_id', parentId),
    (client as any).from(LIVE_PARENT_LINK_VIEW).select('athlete_id').eq('parent_id', parentId),
  ]);
  if (allError || liveError) return null;
  const live = new Set(((liveLinks ?? []) as Array<{ athlete_id: string }>).map((r) => r.athlete_id));
  return new Set(
    ((allLinks ?? []) as Array<{ athlete_id: string }>).map((r) => r.athlete_id).filter((id) => !live.has(id))
  );
}

/**
 * #5915 PR 3: every (parent, athlete) link of these adults that the family
 * removed — rows the table holds that the live view hides. For message code
 * that holds a SAVED recipient list and must drop removed adults at send time.
 * Service-role client only (the view is not granted to users); null on a read
 * error, so callers can fail closed.
 */
export async function getRevokedLinksForParents(
  client: SupabaseClient<any, any, any>,
  parentIds: readonly string[]
): Promise<Array<{ parent_id: string; athlete_id: string }> | null> {
  const read = (source: ParentLinkReadSource) =>
    fetchAllInChunks<{ id: string; parent_id: string; athlete_id: string }>(parentIds, (chunk, from, to) =>
      (client as any)
        .from(source)
        .select('id, parent_id, athlete_id')
        .in('parent_id', chunk)
        .order('id', { ascending: true })
        .range(from, to)
    );
  const [all, live] = await Promise.all([read(PARENT_LINK_TABLE), read(LIVE_PARENT_LINK_VIEW)]);
  if (all.error || live.error) return null;
  const liveIds = new Set(live.rows.map((r) => r.id));
  return all.rows.filter((r) => !liveIds.has(r.id)).map(({ parent_id, athlete_id }) => ({ parent_id, athlete_id }));
}

/**
 * The parent billing lists (invoices, payments, automatic payments) drop the
 * rows of athletes the caller was removed from. Returns null when the lookup
 * failed; the route must refuse (503), never show the unfiltered rows.
 *
 * A row with no athlete_id (a multi-athlete or guest bill) is kept: it names no
 * one athlete to test. The database rule has the same scope.
 *
 * Stricter than the database rule on one point, on purpose: the database also
 * lets an adult who is active staff at the athlete's gym keep seeing it. Here
 * that adult's own billing list still hides it on a service-role read. This
 * hides more, never less.
 */
export async function dropRowsForRevokedAthletes<T extends { athlete_id?: string | null }>(
  client: SupabaseClient<any, any, any>,
  parentId: string,
  rows: T[]
): Promise<T[] | null> {
  const revoked = await getRevokedAthleteIdsForParent(client, parentId);
  if (revoked === null) return null;
  if (revoked.size === 0) return rows;
  return rows.filter((r) => !r.athlete_id || !revoked.has(r.athlete_id));
}

/**
 * One row the caller owns (a bill, a booking, a signature request) about one
 * athlete: 'revoked' when the caller was removed from that athlete's family,
 * 'error' when that could not be checked. Routes answer 404 on 'revoked' (the
 * same as a row they cannot see) and refuse on 'error'. A row with no athlete
 * is 'ok': it names no one athlete to test.
 *
 * Like getRevokedAthleteIdsForParent this reads nothing for a user client;
 * a route whose table the database rule does not cover passes a service-role
 * client so the check still runs.
 */
export async function checkAthleteNotRevoked(
  client: SupabaseClient<any, any, any>,
  parentId: string,
  athleteId: string | null | undefined
): Promise<'ok' | 'revoked' | 'error'> {
  if (!athleteId) return 'ok';
  const revoked = await getRevokedAthleteIdsForParent(client, parentId);
  if (revoked === null) return 'error';
  return revoked.has(athleteId) ? 'revoked' : 'ok';
}

/**
 * The athletes a bill is about: its athlete_id, else the athletes named in
 * metadata.enrollment_details (a multi-athlete enrollment bill). Empty when
 * the bill names no athlete.
 */
export function invoiceAthleteIds(invoice: { athlete_id?: string | null; metadata?: unknown }): string[] {
  if (invoice.athlete_id) return [invoice.athlete_id];
  const details = (invoice.metadata as { enrollment_details?: unknown } | null)?.enrollment_details;
  if (!Array.isArray(details)) return [];
  const ids = new Set<string>();
  for (const detail of details) {
    const id = (detail as { athlete_id?: unknown } | null)?.athlete_id;
    if (typeof id === 'string' && id) ids.add(id);
  }
  return [...ids];
}

/**
 * #5915 PR 2c: a row with no athlete column (a multi-athlete bill, a payment
 * with no athlete, a payment-plan auto-pay) is hidden from a removed adult
 * when every athlete it is about — resolved by the caller — is one they were
 * removed from. A row about no athlete at all stays.
 */
export function isOnlyAboutRevokedAthletes(athleteIds: string[], revoked: ReadonlySet<string>): boolean {
  return athleteIds.length > 0 && athleteIds.every((id) => revoked.has(id));
}
